import type { Period, PortalDog, StayQuote } from '@bbbk/schema';
import { Avatar } from '../components/common';
import { G } from './glyphs';
import { Tag } from './Tag';
import { BookBar, BookPage, RangeNotes } from './RequestChrome';
import { PendingButton } from '../components/common/PendingButton';
import { ReachFailure } from './ReachFailure';
import { dogNames, longDay, money, splitWidth, type DogStayAnswer, type RequestFailure } from './requestRules';

// `c7r` / `c7f` — step 3, check and send (flow 2.1, R-REQ-5, AC8, AC9).
//
// The request as ONE object: the ticket (who, the two handovers, the nights),
// what Patricia needs per dog as chips, and the price as a receipt — the stay
// in full, the deposit with *once accepted* on it, the balance muted. Under
// the deposit minimum the bar is whole and the cell says *In full* (decision
// 4). Nothing is paid here and nothing is held: the one-row note above the
// button says so — *Nothing to pay yet · deposit due once accepted*.

const initialOf = (name: string) => (name.trim()[0] ?? '?').toUpperCase();

export interface RequestReviewStepProps {
  dogs: PortalDog[];
  answers: Record<string, DogStayAnswer>;
  checkIn: string;
  checkOut: string;
  dropOff: Period;
  collection: Period;
  quote: StayQuote;
  sending: boolean;
  failure: RequestFailure | null;
  onSend: () => void;
  onBack: () => void;
  onRetry: () => void;
  onClose: () => void;
}

function Chips({ a }: { a: DogStayAnswer | undefined }) {
  const meds = a?.meds === 'yes';
  const apart = !!a?.keepApart;
  if (!meds && !apart) return <Tag tone="neutral" glyph={G.tick({ size: 12 })}>No requirements</Tag>;
  return (
    <>
      {meds && <Tag tone="meds" glyph={G.pill({ size: 12 })}>Meds</Tag>}
      {apart && <Tag tone="apart" glyph={G.flag({ size: 12 })}>Keep apart</Tag>}
    </>
  );
}

export function RequestReviewStep(p: RequestReviewStepProps) {
  const names = p.dogs.map((d) => d.name);
  const breeds = p.dogs.map((d) => d.breed).filter((b): b is string => !!b);
  const q = p.quote;
  const nightsWord = `${q.nights} ${q.nights === 1 ? 'night' : 'nights'}`;

  return (
    <BookPage onSubmit={() => !p.sending && p.onSend()} onClose={p.onClose} busy={p.sending}>
      <BookBar step={3} onBack={p.onBack} busy={p.sending} />
      <h1 className="cp-h1">Your request</h1>

      <p className="cp-eyebrow cs-eyebrow">Your stay</p>
      <div className="cp-card cs-ticket">
        <div className="cs-who">
          <span className="cs-avs">
            {p.dogs.map((d) => (
              <Avatar key={d.id} name={d.name} id={d.id} size={44} kind="dog" initials={initialOf(d.name)} />
            ))}
          </span>
          <div>
            <b>{dogNames(names)}</b>
            {breeds.length > 0 && <small>{breeds.join(' · ')}</small>}
          </div>
        </div>
        <div className="cs-legs">
          <div className="cs-leg">
            <span className="cp-eyebrow">Drop off</span>
            <b>{longDay(p.checkIn)}</b>
            <small>
              {p.dropOff === 'am' ? G.sunrise({ size: 13 }) : G.sunset({ size: 13 })}
              {p.dropOff === 'am' ? 'Morning' : 'Afternoon'}
            </small>
          </div>
          <div className="cs-arc">
            {G.arrowRight({ size: 18 })}
            <span>{nightsWord}</span>
          </div>
          <div className="cs-leg r">
            <span className="cp-eyebrow">Collect</span>
            <b>{longDay(p.checkOut)}</b>
            <small>
              {p.collection === 'am' ? G.sunrise({ size: 13 }) : G.sunset({ size: 13 })}
              {p.collection === 'am' ? 'Morning' : 'Afternoon'}
            </small>
          </div>
        </div>
      </div>

      <p className="cp-eyebrow cs-eyebrow">Care</p>
      <div className="cp-card cs-care">
        {p.dogs.map((d) => (
          <div key={d.id} className="cs-dog">
            <Avatar name={d.name} id={d.id} size={30} kind="dog" initials={initialOf(d.name)} />
            <b>{d.name}</b>
            <span className="cs-chips">
              <Chips a={p.answers[d.id]} />
            </span>
          </div>
        ))}
      </div>

      <p className="cp-eyebrow cs-eyebrow">Price</p>
      <div className="cp-card cs-money">
        <div className="ln total">
          <span className="k">
            Stay<small>{dogNames(names)} · {nightsWord}</small>
          </span>
          <span className="amt">{money(q.total)}</span>
        </div>
        <div className={'cs-split' + (q.inFull ? ' full' : '')}>
          <i className="bar">
            <b style={{ width: splitWidth(q.deposit, q.total) }}></b>
          </i>
          <div className="cells">
            <div>
              <span className="cp-eyebrow">{q.inFull ? 'In full' : 'Deposit'}</span>
              <span className="v">{money(q.deposit)}</span>
            </div>
            <div className="r">
              {q.inFull ? (
                <span className="cs-why">Payment in full required for stays under {money(q.depositMinimum)}.</span>
              ) : (
                <>
                  <span className="cp-eyebrow">Due</span>
                  <span className="v">{money(q.balance)}</span>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="cp-actions">
        {/* NOTHING PAID, NOTHING HELD — in the notes' own one-row form and in
            the pinned bar, like step 2's (Freddi, 2026-10-08), right above the
            button it is about. */}
        <RangeNotes notes={[{ tone: 'mute', headline: 'Nothing to pay yet', body: q.inFull ? 'pay in full once accepted' : 'deposit due once accepted' }]} />
        <PendingButton type="submit" className="cp-btn primary" icon={G.arrowRight({ size: 15 })} busy={p.sending}>
          Submit request
        </PendingButton>
        {/* Under the button, and pinned with it — a failure the customer has
            to scroll to find is not a message. */}
        {p.failure === 'unreachable' && <ReachFailure what="send" onRetry={p.onRetry} busy={p.sending} />}
        {p.failure === 'busy' && (
          <p className="cp-fail" role="alert">
            You’ve sent a few in a row. Wait a minute, then try again.
          </p>
        )}
      </div>
    </BookPage>
  );
}
